import { Trans } from 'react-i18next';
import FilterIcon from '../../assets/filter.png';
import ModalComponent from '../../component/modal/modal';
import { renderFormRow } from '../../component/userManagementFilter/helper';
import DatePicker from '../../component/datePicker/datePicker';
import { DateRange } from 'react-day-picker';
import { Mode } from '../../component/datePicker/datePicker.type';
import { SweepFilters, SweepsTableFilterProps } from './sweeps.type';
import { useEffect, useMemo, useState } from 'react';
import DropDownInput from '../../component/dropDownInput/dropDownInput';
import { HarmObserved, Sweep, SweepStatus, SweepType } from '@inplay/models';
import TimePicker from '../../component/timePicker/timePicker';
import { Option } from '../../component/dropDownInput/dropDownInput.type';
import { generateOpeningHourOptions, InitalFilters } from './sweepHelper';
import { useSweepsSummary } from '../../hooks/useSweepData';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import styles from './sweeps.module.css';

const SweepsTableFilter = (props: SweepsTableFilterProps) => {
  const { getVenueBranchOpeningHours } = useSweepsSummary({
    startTime: undefined,
    endTime: undefined,
    venueBranchIds: [],
  });
  const [hourlyScheduleOptions, setHourlyScheduleOptions] = useState<Option[]>(
    [],
  );
  const [filters, setFilters] = useState<SweepFilters>(InitalFilters);
  const { isTablet } = useResponsiveLayout();

  useEffect(() => {
    setFilters(props.filters);
  }, [props.filters]);

  useEffect(() => {
    const fetchOpeningHours = async () => {
      if (props.selectedBranchIDs.length === 0) return;

      try {
        const openingHours = await getVenueBranchOpeningHours(
          props.selectedBranchIDs[0],
        );

        const options = generateOpeningHourOptions(
          filters.dateRange.from,
          openingHours,
        );

        setHourlyScheduleOptions(options);
      } catch (error) {
        setHourlyScheduleOptions([]);
      }
    };

    fetchOpeningHours();
  }, [props.selectedBranchIDs, filters.dateRange.from]);

  const completedByOptions: Option[] = useMemo(() => {
    if (!props.sweepsSummary) return [];

    const optionSet: Set<string> = new Set<string>();

    return props.sweepsSummary
      .map((sweep: Sweep) => {
        if (
          !sweep.completedBy ||
          !sweep.completedBy.firstName ||
          !sweep.completedBy.lastName
        ) {
          return undefined;
        }

        const fullName: string =
          sweep.completedBy.firstName + ' ' + sweep.completedBy.lastName;

        if (optionSet.has(fullName)) return undefined;
        optionSet.add(fullName);

        return {
          label: fullName,
          value: sweep.completedBy._id,
        };
      })
      .filter(Boolean) as Option[];
  }, [props.sweepsSummary]);

  return (
    <>
      <div
        onClick={() => props.toggleFilterModal(!props.isOpenUserModal)}
        className="filter-container mr-3  mt-1 relative inline-block cursor-pointer"
      >
        <img src={FilterIcon} alt="Settings" className="w-3 h-3" />
      </div>
      <ModalComponent
        open={props.isOpenUserModal}
        shouldCloseOnOutsideClick={true}
        onToggle={() => {}}
        onFooterButton1Click={() => {
          props.handleClearFilter();
        }}
        onFooterButton2Click={() => {
          props.handleFilterApply(filters);
        }}
        dialogClassName={
          isTablet ? styles.sweepTableFilterTablet : 'top-[190px] w-[600px]'
        }
        title={<Trans i18nKey="userManagement.userFilter.filter" />}
        footerButton1Text={
          <Trans i18nKey="userManagement.userFilter.clearAll" />
        }
        footerButton2Text={<Trans i18nKey="userManagement.userFilter.apply" />}
        onClose={() => props.toggleFilterModal(false)}
        showFooter2Button
        showFooter1Button
        isShowClose
        footerButton1WrapperStyle={isTablet ? 'w-[300px]' : ''}
        footerButton2WrapperStyle={isTablet ? 'w-[300px]' : ''}
      >
        <div className="z-20">
          {renderFormRow(
            <Trans i18nKey="sweeps.filters.date" />,
            <DatePicker
              mode={Mode.Single}
              handleApply={(range: DateRange) =>
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  dateRange: range,
                }))
              }
              finalRange={filters.dateRange}
              className={'top-[340px]'}
            />,
          )}
        </div>
        {renderFormRow(
          <Trans i18nKey="sweeps.filters.time" />,
          <div className="flex items-center w-full gap-4">
            <TimePicker
              selectedTime={filters.time.from || ''}
              placeholder={<Trans i18nKey={'sweeps.selectPlayers.from'} />}
              timeBreak={60}
              onTimeChange={(time: string) => {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  time: { ...prev.time, from: time === '' ? undefined : time },
                  hours: time === '' ? prev.hours : [],
                }));
              }}
              maxTime={filters.time.until}
              isDisabled={filters.hours.length !== 0}
            />
            <span>—</span>
            <TimePicker
              selectedTime={filters.time.until || ''}
              placeholder={<Trans i18nKey={'sweeps.selectPlayers.until'} />}
              timeBreak={60}
              onTimeChange={(time: string) => {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  time: { ...prev.time, until: time === '' ? undefined : time },
                  hours: time === '' ? prev.hours : [],
                }));
              }}
              minTime={filters.time.from}
              isDisabled={filters.hours.length !== 0}
            />
          </div>,
        )}
        {renderFormRow(
          <Trans i18nKey="sweeps.filters.hours" />,
          <DropDownInput
            placeholder={<Trans i18nKey={'userManagement.select'} />}
            isMulti
            options={hourlyScheduleOptions}
            selected={filters.hours}
            onSelect={(selected: string | string[]) => {
              if (Array.isArray(selected)) {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  hours: selected as string[],
                  time:
                    selected.length === 0
                      ? prev.time
                      : { from: undefined, until: undefined },
                }));
              } else {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  hours: [selected as string],
                  time:
                    selected.length === 0
                      ? prev.time
                      : { from: undefined, until: undefined },
                }));
              }
            }}
            disabled={!!filters.time.from || !!filters.time.until}
          />,
        )}
        {renderFormRow(
          <Trans i18nKey="sweeps.filters.status" />,
          <DropDownInput
            placeholder={<Trans i18nKey={'userManagement.select'} />}
            isMulti
            options={[
              { label: 'Complete', value: SweepStatus.Completed },
              { label: 'Missed', value: SweepStatus.Missed },
            ]}
            selected={filters.status}
            onSelect={(selected: string | string[]) => {
              if (Array.isArray(selected)) {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  status: selected as SweepStatus[],
                }));
              } else {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  status: [selected as SweepStatus],
                }));
              }
            }}
          />,
        )}
        {renderFormRow(
          <Trans i18nKey="sweeps.filters.sweepType" />,
          <DropDownInput
            placeholder={<Trans i18nKey={'userManagement.select'} />}
            isMulti
            options={[
              { label: 'Obligatory', value: SweepType.Obligatory },
              { label: 'Voluntary', value: SweepType.Voluntary },
            ]}
            selected={filters.sweepType}
            onSelect={(selected: string | string[]) => {
              if (Array.isArray(selected)) {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  sweepType: selected as SweepType[],
                }));
              } else {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  sweepType: [selected as SweepType],
                }));
              }
            }}
          />,
        )}
        {renderFormRow(
          <Trans i18nKey="sweeps.filters.harmObserved" />,
          <DropDownInput
            placeholder={<Trans i18nKey={'userManagement.select'} />}
            options={[
              { label: 'Yes', value: HarmObserved.YES },
              { label: 'No', value: HarmObserved.NO },
            ]}
            selected={filters.harmObserved}
            onSelect={(selected: string | string[]) => {
              if (Array.isArray(selected)) {
                return;
              } else {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  harmObserved:
                    (((selected as HarmObserved) &&
                      (selected === prev.harmObserved
                        ? undefined
                        : selected)) as HarmObserved) || undefined,
                }));
              }
            }}
          />,
        )}
        {renderFormRow(
          <Trans i18nKey="sweeps.filters.completedBy" />,
          <DropDownInput
            placeholder={<Trans i18nKey={'userManagement.select'} />}
            isMulti
            options={completedByOptions}
            selected={filters.completedBy}
            onSelect={(selected: string | string[]) => {
              if (Array.isArray(selected)) {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  completedBy: selected as string[],
                }));
              } else {
                setFilters((prev: SweepFilters) => ({
                  ...prev,
                  completedBy: [selected as string],
                }));
              }
            }}
          />,
        )}
      </ModalComponent>
    </>
  );
};

export default SweepsTableFilter;
